import React, { Fragment, useState, useEffect } from 'react';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  useMediaQuery
} from '@material-ui/core';
import { ExpandLess, ExpandMore } from '@material-ui/icons';
import DynamicLogo from 'components/DynamicLogo';
import { ReactComponent as DashboardIcon } from './assets/dashboard.svg';
import { ReactComponent as FormIcon } from './assets/form.svg';
import { ReactComponent as ReportIcon } from './assets/report.svg';
import { ReactComponent as CalendarIcon } from './assets/calendar.svg';
import { ReactComponent as WhistleIcon } from './assets/whistle.svg';
import { ReactComponent as ProfileIcon } from './assets/profile.svg';
import { ReactComponent as CloseSidebarIcon } from './assets/close-sidebar.svg';
import { ReactComponent as LogoutIcon } from './assets/logout.svg';
import { ReactComponent as WorkoutIcon } from './assets/workout.svg';
import { ReactComponent as DietIcon } from './assets/diet.svg';
import { ReactComponent as EducationalResourceIcon } from './assets/educational_resources.svg';
import { ReactComponent as VideoIcon } from './assets/video.svg';
import { ReactComponent as FileIcon } from './assets/file.svg';
import { ReactComponent as FeedbackIcon } from './assets/feedback.svg';
import { ReactComponent as ConfigurationIcon } from './assets/config.svg';
import { ReactComponent as SharingLogsIcon } from './assets/sharing_logs.svg';
import { ReactComponent as DataManagementIcon } from './assets/data-management.svg';
import { Scrollbars } from 'react-custom-scrollbars';

import layoutStore from 'stores/layout.store';
import userStore from 'stores/user.store';
import useStyles from './style';
import { getModuleConfig, hasModuleAccess } from 'libs/access/access.lib';

const Sidebar = () => {
  const [isFormMenuOpen, setIsFormMenuOpen] = useState(false);
  const [isReportMenuOpen, setIsReportMenuOpen] = useState(false);
  const [isWorkoutMenuOpen, setIsWorkoutMenuOpen] = useState(false);
  const [isConfigurationMenuOpen, setIsConfigurationMenuOpen] = useState(false);
  const classes = useStyles();
  const { push } = useHistory();
  const { pathname } = useLocation();
  const isDrawerOpen = layoutStore.getIsDrawerOpen();
  const isNotDesktop = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const user = userStore.getUser();
  const moduleConfig = getModuleConfig(user);
  const userRoleId = +user.role.id;

  const canSeeArs = hasModuleAccess(user, moduleConfig, 'ars');
  const canSeeArsConfig = hasModuleAccess(user, moduleConfig, 'ars-config');
  const canSeeHydration = hasModuleAccess(user, moduleConfig, 'hydration');
  const canSeeForms = canSeeArs || canSeeHydration || canSeeArsConfig;

  const canSeePlayerReadinessReport = hasModuleAccess(user, moduleConfig, 'player-readiness-report');
  const canSeeOverallReadiness = hasModuleAccess(user, moduleConfig, 'overall-readiness');
  const canSeeReadinessProgress = hasModuleAccess(user, moduleConfig, 'readiness-progress');
  const canSeeArsReports = hasModuleAccess(user, moduleConfig, 'ars-reports');
  const canSeeArsPlayerComparison = hasModuleAccess(user, moduleConfig, 'ars-player-comparison');
  const canSeeArsTimeSeries = hasModuleAccess(user, moduleConfig, 'ars-time-series');
  const canSeeArsSummaryReports = hasModuleAccess(user, moduleConfig, 'ars-summary-reports');
  const canSeePlayerProgressReport = hasModuleAccess(user, moduleConfig, 'player-progress-report');
  const canSeeReports =
    canSeePlayerReadinessReport
    || canSeeOverallReadiness
    || canSeeReadinessProgress
    || canSeeArsReports
    || canSeeArsPlayerComparison
    || canSeeArsTimeSeries
    || canSeeArsSummaryReports
    || canSeePlayerProgressReport;

  const canSeeEducationalResources = hasModuleAccess(user, moduleConfig, 'educational-resources');
  const canSeeFuelingManager = hasModuleAccess(user, moduleConfig, 'fueling-manager');
  const canSeeFileManager = hasModuleAccess(user, moduleConfig, 'file-manager');
  const canSeeEventManager = hasModuleAccess(user, moduleConfig, 'event-manager');
  const canSeeSportManagement = hasModuleAccess(user, moduleConfig, 'sport-management');
  const canSeeSharingRequests = hasModuleAccess(user, moduleConfig, 'sharing-requests');
  const canSeeDataExports = hasModuleAccess(user, moduleConfig, 'data-exports');
  const canseeExercises = hasModuleAccess(user, moduleConfig, 'exercises');
  const canseeWorkouts = hasModuleAccess(user, moduleConfig, 'workouts');
  const canseeWorkoutAssignments = hasModuleAccess(user, moduleConfig, 'workout-assignments');
  const canSeeWorkoutManager = canseeExercises || canseeWorkouts || canseeWorkoutAssignments;

  useEffect(() => {
    if (isNotDesktop) {
      layoutStore.setIsDrawerOpen(false);
    }
  }, [isNotDesktop]);

  return (
    <Drawer
      variant='permanent'
      classes={{
        paper: clsx(
          classes.drawerPaper,
          !isDrawerOpen && classes.drawerPaperClose
        )
      }}
      open={isDrawerOpen}
    >
      <Box className={classes.toolbarIcon}>
        {isDrawerOpen && (
          <Fragment>
            <DynamicLogo className={classes.logo} onClick={() => push('/')} />
            <IconButton onClick={() => layoutStore.setIsDrawerOpen(false)}>
              <CloseSidebarIcon className={classes.sideIcons} />
            </IconButton>
          </Fragment>
        )}
      </Box>
      <Scrollbars className={classes.mainSidebar}>
        <List>
          <ListItem
            button
            onClick={() => push('/')}
            selected={pathname === '/dashboard'}
          >
            <ListItemIcon>
              <DashboardIcon className={classes.sideIcons} />
            </ListItemIcon>
            <ListItemText primary='Dashboard' />
          </ListItem>
          {canSeeForms && (
            <>
              <ListItem button onClick={() => setIsFormMenuOpen((value) => !value)}>
                <ListItemIcon>
                  <FormIcon className={classes.sideIcons} />
                </ListItemIcon>
                <ListItemText primary='Forms' />
                {isFormMenuOpen ? <ExpandLess /> : <ExpandMore />}
              </ListItem>
              <Collapse in={isFormMenuOpen} timeout='auto' unmountOnExit>
                <List disablePadding>
                  {canSeeArs ? (
                    <ListItem
                      button
                      className={classes.nested}
                      onClick={() => push('/forms/ars')}
                      selected={pathname === '/forms/ars'}
                    >
                      <ListItemIcon></ListItemIcon>
                      <ListItemText primary='ARS' />
                    </ListItem>
                  ) : (
                    ''
                  )}
                  {userRoleId === 1 ? (
                    <ListItem
                      button
                      className={classes.nested}
                      onClick={() => push('/forms/ars/config')}
                      selected={pathname === '/forms/ars/config'}
                    >
                      <ListItemIcon></ListItemIcon>
                      <ListItemText primary='ARS Configuration' />
                    </ListItem>
                  ) : (
                    ''
                  )}
                  {canSeeHydration ? (
                    <ListItem
                      button
                      className={classes.nested}
                      onClick={() => push('/forms/hydration')}
                      selected={pathname === '/forms/hydration'}
                    >
                      <ListItemIcon></ListItemIcon>
                      <ListItemText primary='Hydration' />
                    </ListItem>
                  ) : (
                    ''
                  )}
                </List>
              </Collapse>
            </>
          )}
          {canSeeReports && (
            <>
              <ListItem
                button
                onClick={() => setIsReportMenuOpen((value) => !value)}
              >
                <ListItemIcon>
                  <ReportIcon className={classes.sideIcons} />
                </ListItemIcon>
                <ListItemText primary='Reports' />
                {isReportMenuOpen ? <ExpandLess /> : <ExpandMore />}
              </ListItem>
              <Collapse in={isReportMenuOpen} timeout='auto' unmountOnExit>
                <List disablePadding>
                  {canSeePlayerReadinessReport ? (
                    <ListItem
                      button
                      className={classes.nested}
                      onClick={() =>
                        push('/reports/readiness-reports/player-readiness-report')
                      }
                      selected={
                        pathname ===
                        '/reports/readiness-reports/player-readiness-report'
                      }
                    >
                      <ListItemIcon></ListItemIcon>
                      <ListItemText primary='Player Readiness Report' />
                    </ListItem>
                  ) : (
                    ''
                  )}
                  {canSeeOverallReadiness ? (
                    <ListItem
                      button
                      className={classes.nested}
                      onClick={() =>
                        push('/reports/readiness-reports/overall-readiness')
                      }
                      selected={
                        pathname === '/reports/readiness-reports/overall-readiness'
                      }
                    >
                      <ListItemIcon></ListItemIcon>
                      <ListItemText primary='Overall Readiness' />
                    </ListItem>
                  ) : (
                    ''
                  )}
                  {canSeeReadinessProgress ? (
                    <ListItem
                      button
                      className={classes.nested}
                      onClick={() =>
                        push('/reports/readiness-reports/readiness-progress')
                      }
                      selected={
                        pathname === '/reports/readiness-reports/readiness-progress'
                      }
                    >
                      <ListItemIcon></ListItemIcon>
                      <ListItemText primary='Readiness Progress' />
                    </ListItem>
                  ) : (
                    ''
                  )}

                  {canSeeArsReports ? (
                    <ListItem
                      button
                      className={classes.nested}
                      onClick={() => push('/reports/ars-reports/base-report')}
                      selected={pathname === '/reports/ars-reports/base-report'}
                    >
                      <ListItemIcon></ListItemIcon>
                      <ListItemText primary='ARS Reports' />
                    </ListItem>
                  ) : (
                    ''
                  )}
                  {canSeeArsPlayerComparison ? (
                    <ListItem
                      button
                      className={classes.nested}
                      onClick={() =>
                        push('/reports/ars-reports/player-comparison')
                      }
                      selected={
                        pathname === '/reports/ars-reports/player-comparison'
                      }
                    >
                      <ListItemIcon></ListItemIcon>
                      <ListItemText primary='ARS Player Comparison' />
                    </ListItem>
                  ) : (
                    ''
                  )}
                  {canSeeArsTimeSeries ? (
                    <ListItem
                      button
                      className={classes.nested}
                      onClick={() => push('/reports/ars-reports/time-series')}
                      selected={pathname === '/reports/ars-reports/time-series'}
                    >
                      <ListItemIcon></ListItemIcon>
                      <ListItemText primary='ARS Time Series' />
                    </ListItem>
                  ) : (
                    ''
                  )}
                  {canSeeArsSummaryReports ? (
                    <ListItem
                      button
                      className={classes.nested}
                      onClick={() => push('/reports/ars-reports/ars-summary-reports')}
                      selected={pathname === '/reports/ars-reports/ars-summary-reports'}
                    >
                      <ListItemIcon></ListItemIcon>
                      <ListItemText primary='ARS Summary Reports' />
                    </ListItem>
                  ) : (
                    ''
                  )}
                  {canSeePlayerProgressReport ? (
                    <ListItem
                      button
                      className={classes.nested}
                      onClick={() => push('/reports/ars-reports/player-progress-report')}
                      selected={pathname === '/reports/ars-reports/player-progress-report'}
                    >
                      <ListItemIcon></ListItemIcon>
                      <ListItemText primary='Player Progress Report' />
                    </ListItem>
                  ) : (
                    ''
                  )}
                  {/* <ListItem button className={classes.nested}>
                <ListItemIcon></ListItemIcon>
                <ListItemText primary='ARS Base' />
              </ListItem>
              <ListItem button className={classes.nested}>
                <ListItemIcon></ListItemIcon>
                <ListItemText primary='Antagonis Agonist' />
              </ListItem> */}
                </List>
              </Collapse>
            </>
          )}

          {canSeeWorkoutManager && (
            <>
              <ListItem
                button
                onClick={() => setIsWorkoutMenuOpen((value) => !value)}
              >
                <ListItemIcon>
                  <WorkoutIcon className={classes.sideIcons} />
                </ListItemIcon>
                <ListItemText primary='Workout Manager' />
                {isWorkoutMenuOpen ? <ExpandLess /> : <ExpandMore />}
              </ListItem>

              <Collapse in={isWorkoutMenuOpen} timeout='auto' unmountOnExit>
                <List disablePadding>
                  {canseeExercises ? (
                    <ListItem
                      button
                      className={classes.nested}
                      onClick={() => push('/workout-manager/exercises')}
                      selected={pathname === '/workout-manager/exercises'}
                    >
                      <ListItemIcon></ListItemIcon>
                      <ListItemText primary='Exercises' />
                    </ListItem>
                  ) : (
                    ''
                  )}
                </List>
                {canseeWorkouts ? (
                  <List disablePadding>
                    <ListItem
                      button
                      className={classes.nested}
                      onClick={() => push('/workout-manager/workouts')}
                      selected={pathname === '/workout-manager/workouts'}
                    >
                      <ListItemIcon></ListItemIcon>
                      <ListItemText primary='Workouts' />
                    </ListItem>
                  </List>
                ) : (
                  ''
                )}
                {canseeWorkoutAssignments ? (
                  <List disablePadding>
                    <ListItem
                      button
                      className={classes.nested}
                      onClick={() => push('/workout-manager/workout-assignments')}
                      selected={pathname === '/workout-manager/workout-assignments'}
                    >
                      <ListItemIcon></ListItemIcon>
                      <ListItemText primary='Workout Assignments' />
                    </ListItem>
                  </List>
                ) : (
                  ''
                )}
              </Collapse>
            </>
          )}

          {canSeeEducationalResources ? (
            <ListItem
              button
              onClick={() => push('/educational-resources')}
              selected={pathname === '/educational-resources'}
            >
              <ListItemIcon>
                <EducationalResourceIcon className={classes.sideIcons} />
              </ListItemIcon>
              <ListItemText primary='Educational Resources' />
            </ListItem>
          ) : (
            ''
          )}

          {canSeeFuelingManager ? (
            <ListItem
              button
              onClick={() => push('/fueling-manager/fueling-plans')}
              selected={pathname === '/fueling-manager/fueling-plans'}
            >
              <ListItemIcon>
                <DietIcon className={classes.sideIcons} />
              </ListItemIcon>
              <ListItemText primary='Fueling Manager' />
            </ListItem>
          ) : (
            ''
          )}
          {userRoleId === 1 ? (
            <ListItem
              button
              onClick={() => push('/video-manager/video-feeds')}
              selected={pathname === '/video-manager/video-feeds'}
            >
              <ListItemIcon>
                <VideoIcon className={classes.sideIcons} />
              </ListItemIcon>
              <ListItemText primary='Video Manager' />
            </ListItem>
          ) : (
            ''
          )}
          {canSeeFileManager ? (
            <ListItem
              button
              onClick={() => push('/file-manager')}
              selected={pathname === '/file-manager'}
            >
              <ListItemIcon>
                <FileIcon className={classes.sideIcons} />
              </ListItemIcon>
              <ListItemText primary='File Manager' />
            </ListItem>
          ) : (
            ''
          )}
          {/* <ListItem button onClick={() => push('/')}>
            <ListItemIcon>
              <CalendarIcon />
            </ListItemIcon>
            <ListItemText primary='Calendar' />
          </ListItem>
          <ListItem button onClick={() => push('/')}>
            <ListItemIcon>
              <MessageIcon />
            </ListItemIcon>
            <ListItemText primary='Message' />
          </ListItem> */}
          {canSeeEventManager ? (
            <ListItem
              button
              onClick={() => push('/event-manager')}
              selected={pathname === '/event-manager'}
            >
              <ListItemIcon>
                <CalendarIcon className={classes.sideIcons} />
              </ListItemIcon>
              <ListItemText primary='Events Manager' />
            </ListItem>
          ) : (
            ''
          )}
          {canSeeSportManagement ? (
            <ListItem
              button
              onClick={() => push('/sport-management')}
              selected={pathname === '/sport-management'}
            >
              <ListItemIcon>
                <WhistleIcon className={classes.sideIcons} />
              </ListItemIcon>
              <ListItemText primary='Sports Management' />
            </ListItem>
          ) : (
            ''
          )}
          {[1, 2, 3].includes(userRoleId) ? (
            <ListItem
              button
              onClick={() => push('/user-management')}
              selected={pathname === '/user-management'}
            >
              <ListItemIcon>
                <ProfileIcon className={classes.sideIcons} />
              </ListItemIcon>
              <ListItemText primary='User Management' />
            </ListItem>
          ) : (
            ''
          )}
          {
            userRoleId === 1 ? (
              <>
                <ListItem button onClick={() => setIsConfigurationMenuOpen((value) => !value)}>
                  <ListItemIcon>
                    <ConfigurationIcon className={classes.sideIcons} />
                  </ListItemIcon>
                  <ListItemText primary='Configuration' />
                  {isConfigurationMenuOpen ? <ExpandLess /> : <ExpandMore />}
                </ListItem>
                <Collapse in={isConfigurationMenuOpen} timeout='auto' unmountOnExit>
                  <List disablePadding>
                    <ListItem
                      button
                      className={classes.nested}
                      onClick={() => push('/configuration/access-templates')}
                      selected={pathname === '/configuration/access-templates'}
                    >
                      <ListItemIcon></ListItemIcon>
                      <ListItemText primary='Access Templates' />
                    </ListItem>
                    <ListItem
                      button
                      className={classes.nested}
                      onClick={() => push('/configuration/notification-mappings')}
                      selected={pathname === '/configuration/notification-mappings'}
                    >
                      <ListItemIcon></ListItemIcon>
                      <ListItemText primary='Notification Mappings' />
                    </ListItem>
                    <ListItem
                      button
                      className={classes.nested}
                      onClick={() => push('/configuration/themes')}
                      selected={pathname === '/configuration/themes'}
                    >
                      <ListItemIcon></ListItemIcon>
                      <ListItemText primary='Themes' />
                    </ListItem>
                  </List>
                </Collapse>
              </>
            ) : (
              ''
            )}
          {canSeeSharingRequests ? (
            <ListItem
              button
              className={classes.nested}
              onClick={() => push('/sharing-requests')}
              selected={pathname === '/sharing-requests'}
            >
              <ListItemIcon>
                <SharingLogsIcon className={classes.sideIcons} />
              </ListItemIcon>
              <ListItemText primary='Sharing Logs' />
            </ListItem>
          ) : (
            ''
          )}
          {canSeeDataExports ? (
            <ListItem button onClick={() => push('/data-exports')}>
              <ListItemIcon>
                <DataManagementIcon className={classes.sideIcons} />
              </ListItemIcon>
              <ListItemText primary='Data Exports' />
            </ListItem>
          ) : (
            ''
          )}
          {userRoleId === 1 ? (
            <ListItem
              button
              onClick={() => push('/feedbacks')}
              selected={pathname === '/feedbacks'}
            >
              <ListItemIcon>
                <FeedbackIcon className={classes.sideIcons} />
              </ListItemIcon>
              <ListItemText primary='Feedbacks' />
            </ListItem>
          ) : (
            ''
          )}
        </List>
      </Scrollbars>
      <List className={classes.bottomSidebar}>
        <ListItem button onClick={async () => await userStore.logout()}>
          <ListItemIcon>
            <Box width={30} textAlign='center'>
              <LogoutIcon className={classes.sideIcons} />
            </Box>
          </ListItemIcon>
          <ListItemText primary='Logout' />
        </ListItem>
      </List>
    </Drawer>
  );
};

export default observer(Sidebar);
